import * as am4charts from "@amcharts/amcharts4/charts";
import {am4Settings} from "@/mixins/am4charts/am4Settings";
import * as am4core from "@amcharts/amcharts4/core";

const DEFAULT_MAX = -100000
const DEFAULT_MIN = 100000

export const SideAxis = class {
    constructor() {
        this.create()
    }

    addSeries(series, loadData = false) {
        if (_.isNil(series)) {
            console.error(`SideAxis@addSeries - Unable to add null series.`)
            return
        }

        if (_.find(this.series, series)) {
            return
        }

        if (this.axisInitialized()) {
            this.am4Axis.visible = true
        }

        if (loadData) {
            series.loadData()
        }

        this.series.push(series)
        series.setSideAxis(this)
        series.setupSeries()
    }

    removeSeries(series) {
        if (_.isNil(series) && this.axisInitialized() && this.am4Axis.series.length === 0) {
            return
        }

        // If the Series is not visible on this axis, do nothing
        if (! this.hasSeriesVisible(series)) {
            return
        }

        // Remove the series from this axis's series list
        let newSeries = _.remove(this.series, s => {
            return `${s.id}` === `${series.id}`
        })

        let changesMade = newSeries.length !== this.series.length
        if (! changesMade) {
            return
        }

        series.hide()
        series.reset()

        if (this.axisInitialized() && this.series.length === 0) {
            this.am4Axis.visible = false
        }
    }

    getCurrentProfile() {
        return this.currentProfile
    }

    setCurrentProfile(profile) {
        this.currentProfile = profile
        if (! this.axisInitialized()) {
            return
        }

        // Assign the profile name and units to the axis title
        if (this.currentProfile.units.trim() === "") {
            this.am4Axis.title.text = `${this.currentProfile.name}`
        } else {
            this.am4Axis.title.text = `${this.currentProfile.name} (${this.currentProfile.units})`
        }
    }

    hasSeriesVisible(series) {
        if (! series.isVisible()) {
            return false
        }

        return _.findIndex(this.series, s => {
            return s.id === series.id
        }) !== -1
    }

    checkScalingProfile(profileId) {
        if (_.isNil(this.currentProfile)) {
            return false
        }

        return profileId === this.currentProfile.id
    }

    updateMinMax() {
        this.min = DEFAULT_MIN
        this.max = DEFAULT_MAX
        _.each(this.series, s => {
            this.min = Math.min(this.min, s.min)
            this.max = Math.max(this.max, s.max)
        })

        this.renderAxis()
    }

    getAm4Axis() {
        return this.am4Axis
    }

    renderLeft() {
        this.isLeft = true
        if (this.axisInitialized()) {
            this.am4Axis.renderer.opposite = false
        }

        _.each(this.series, series => {
            series.setAxisLeft()
        })
    }

    renderRight() {
        this.isLeft = false
        if (this.axisInitialized()) {
            this.am4Axis.renderer.opposite = true
        }

        _.each(this.series, series => {
            series.setAxisRight(this.axisIndex)
        })
    }

    buildAndRender(chart) {
        if (this.axisInitialized()) {
            this.dispose()
        }

        // Construct and track axis on chart
        this.am4Axis = new am4charts.ValueAxis()

        // Min/Max Rules for the axis to include the "alarm profile ranges" and to add padding
        this.am4Axis.extraMin = am4Settings.Y_AXIS_SPACE_PERCENT
        this.am4Axis.extraMin = am4Settings.Y_AXIS_SPACE_PERCENT
        this.am4Axis.includeRangesInMinMax = true

        chart.yAxes.push(this.am4Axis)
        this.axisIndex = chart.yAxes.length - 1

        // If this is not the left axis, sync it with the left axis
        if (this.axisIndex !== 0) {
            this.am4Axis.syncWithAxis = chart.yAxes.getIndex(0)
        }

        // Set curent profile information for Axis
        if (! _.isNil(this.currentProfile)) {
            if (this.currentProfile.units.trim() === "") {
                this.am4Axis.title.text = `${this.currentProfile.name}`
            } else {
                this.am4Axis.title.text = `${this.currentProfile.name} (${this.currentProfile.units})`
            }
        }

        if (!this.isLeft) {
            this.renderRight()
        }

        this.renderAxis()
    }

    renderAxis() {
        if (_.isNil(this.am4Axis)) {
            return
        }

        if (this.am4Axis.min === this.am4Axis.max) {
            this.am4Axis.min -= 0.25
            this.am4Axis.max += 0.25
        }

        this.am4Axis.tooltip.disabled = true
        this.am4Axis.renderer.minWidth = 35

        if (this.currentProfile && this.currentProfile.has_text_labels_on_graph) {
            let text_labels_for_graph = this.currentProfile.text_labels_for_graph;
            if (typeof text_labels_for_graph === 'string') {
                text_labels_for_graph = JSON.parse(text_labels_for_graph);
            }
            this.numberToTextMap = text_labels_for_graph;

            this.am4Axis.axisRanges.clear();

            // HIDING THE DEFAULT GRID LINES AND LABELS
            //
            // In theory, we should be able to set the renderer.labels.template.disabled = true
            //   and set the renderer.grid.template.disabled = true to hide the labels and grid lines.
            // However, during building of this feature, it seemed like that had a weird pseudo-race condition
            //   with how AmCharts was initializing the labels. So, instead, we're setting the opacity to 0
            //   which seems to work fine.
            this.am4Axis.renderer.labels.template.opacity = 0;
            this.am4Axis.renderer.grid.template.opacity = 0;

            // The following line is meant to remove the horizontal line that separated the 0 and 1 values at 0.5
            //   because that's not super helpful for our use case. This value was experimentally determined by Jason
            //   and is likely subject to change for different screen sizes etc.
            // GPT suggested listening to the "validated" event and disabling the grid lines that don't have a label
            //   but that didn't seem to work.
            // this.am4Axis.renderer.minGridDistance = 200;

            // Create custom axis ranges and labels
            Object.keys(this.numberToTextMap).forEach(number => {
                let range = this.am4Axis.axisRanges.create();
                range.value = Number(number);
                range.label.text = this.numberToTextMap[number];
                range.label.opacity = 1; // Ensure custom label is visible

                range.grid.strokeOpacity = 1; // Ensure custom grid line is visible
                range.grid.stroke = am4core.color("#ccc");
            });
        } else {
            // Reset to default settings (see HIDING THE DEFAULT GRID LINES AND LABELS)
            this.am4Axis.renderer.labels.template.opacity = 1;
            this.am4Axis.renderer.grid.template.opacity = 1;
        }
    }

    axisInitialized() {
        return !_.isNil(this.am4Axis)
    }

    create() {
        this.series = []
        this.axisIndex = null
        this.isLeft = true
        this.min = DEFAULT_MIN
        this.max = DEFAULT_MAX
        this.currentProfile = null

        this.am4Axis = null
        this.numberToTextMap = {};
    }

    dispose() {
        if (this.axisInitialized()) {
            this.am4Axis.dispose()
        }
    }
}
